import React, { useState, useEffect, useCallback } from 'react';

const ThemeCreator = () => {
  const [themes, setThemes] = useState([]);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  //const process.env.REACT_APP_SOULWARS_API_KEY = 'soulwarz';

  // Listing controls
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('updatedAt'); // 'updatedAt' | 'name'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  // API helper function (memoized to satisfy React Hooks exhaustive-deps)
  const apiRequest = useCallback(async (endpoint, options = {}) => {
    const response = await fetch(`/api${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
      },
      ...options,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'API request failed');
    }

    return response.json();
  }, []);

  const loadThemes = useCallback(async () => {
    try {
      const themeList = await apiRequest('/themes');
      setThemes(themeList);
    } catch (err) {
      setError('Failed to load themes: ' + err.message);
    }
  }, [apiRequest]);

  // Load themes on component mount
  useEffect(() => {
    loadThemes();
  }, [loadThemes]);

  const createNewTheme = async (name, description) => {
    setLoading(true);
    setError('');
    try {
      const theme = await apiRequest('/themes', {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      });
      setThemes([...themes, theme]);
      setCurrentTheme(theme);
      setSuccess('Theme created successfully!');
    } catch (err) {
      setError('Failed to create theme: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectTheme = async (theme) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Fetch complete theme data with all assets
      const fullTheme = await apiRequest(`/themes/${theme.id}`);
      setCurrentTheme(fullTheme);
    } catch (err) {
      setError('Failed to load theme details: ' + err.message);
      // Fallback to basic theme data if fetch fails
      setCurrentTheme(theme);
    } finally {
      setLoading(false);
    }
  };

  // Derived, filtered/sorted/paged themes
  const normalizedSearch = search.trim().toLowerCase();
  const filteredThemes = themes.filter(t => {
    if (!normalizedSearch) return true;
    const name = (t.name || '').toLowerCase();
    const desc = (t.description || '').toLowerCase();
    return name.includes(normalizedSearch) || desc.includes(normalizedSearch) || (t.id || '').toLowerCase().includes(normalizedSearch);
  });

  const sortedThemes = [...filteredThemes].sort((a, b) => {
    let av, bv;
    if (sortBy === 'name') {
      av = (a.name || '').toLowerCase();
      bv = (b.name || '').toLowerCase();
    } else {
      av = new Date(a.updatedAt || a.createdAt || 0).getTime();
      bv = new Date(b.updatedAt || b.createdAt || 0).getTime();
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedThemes.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const pagedThemes = sortedThemes.slice(startIdx, startIdx + pageSize);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, sortBy, sortDir, pageSize]);

  const updateTheme = async (updates) => {
    if (!currentTheme) return;

    setLoading(true);
    try {
      const updatedTheme = await apiRequest(`/themes/${currentTheme.id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      setCurrentTheme(updatedTheme);
      setThemes(themes.map(t => t.id === updatedTheme.id ? updatedTheme : t));
      setSuccess('Theme updated successfully!');
    } catch (err) {
      setError('Failed to update theme: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadAsset = async (assetType, file, extraFields) => {
    if (!currentTheme) return;

    const formData = new FormData();
    formData.append('file', file);
    if (extraFields && typeof extraFields === 'object') {
      Object.entries(extraFields).forEach(([k, v]) => {
        if (v !== undefined && v !== null) formData.append(k, String(v));
      });
    }

    setLoading(true);
    try {
      const result = await fetch(`/api/themes/${currentTheme.id}/assets/${assetType}`, {
        method: 'POST',
        headers: {
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
        body: formData,
      });

      if (!result.ok) {
        const error = await result.json();
        throw new Error(error.error || 'Upload failed');
      }

      const asset = await result.json();

      // Update theme with new asset
      const updatedTheme = { ...currentTheme };
      updatedTheme.assets[assetType].push(asset.filename);
      if (assetType === 'players' && asset.player) {
        const nextPlayers = Array.isArray(updatedTheme.players) ? [...updatedTheme.players] : [];
        nextPlayers.push(asset.player);
        updatedTheme.players = nextPlayers;
        // ensure class present
        if (asset.player.className) {
          const cls = asset.player.className;
          const classes = Array.isArray(updatedTheme.classes) ? [...updatedTheme.classes] : [];
          if (!classes.includes(cls)) classes.push(cls);
          updatedTheme.classes = classes;
        }
      }
      updatedTheme.updatedAt = new Date().toISOString();
      setCurrentTheme(updatedTheme);
      setThemes(themes.map(t => t.id === updatedTheme.id ? updatedTheme : t));
      setSuccess(`${assetType === 'players' ? 'Player' : assetType.slice(0, -1)} saved successfully!`);
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteAsset = async (assetType, filename) => {
    if (!currentTheme) return;

    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;

    setLoading(true);
    try {
      await apiRequest(`/themes/${currentTheme.id}/assets/${assetType}/${filename}`, {
        method: 'DELETE',
      });

      // Update theme by removing asset
      const updatedTheme = { ...currentTheme };
      updatedTheme.assets[assetType] = updatedTheme.assets[assetType].filter(f => f !== filename);
      updatedTheme.updatedAt = new Date().toISOString();
      setCurrentTheme(updatedTheme);
      setThemes(themes.map(t => t.id === updatedTheme.id ? updatedTheme : t));
      setSuccess('Asset deleted successfully!');
    } catch (err) {
      setError('Failed to delete asset: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleIntermissionUpdate = async (intermissions) => {
    try {
      const response = await fetch(`/api/themes/${currentTheme.id}/intermissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
        body: JSON.stringify(intermissions),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update intermissions');
      }

      const updatedIntermissions = await response.json();
      const updatedTheme = { ...currentTheme, intermissions: updatedIntermissions };
      setCurrentTheme(updatedTheme);
      setThemes(themes.map(t => t.id === updatedTheme.id ? updatedTheme : t));
      setSuccess('Intermissions updated successfully!');
    } catch (err) {
      setError('Failed to update intermissions: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Theme Creator</h1>
          <p className="text-gray-600">Create and manage custom themes for SoulWars</p>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}
        {loading && (
          <div className="mb-4 p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded">
            Loading...
          </div>
        )}

        {/* Theme Selection/List */}
        <CollapsibleSection title="Your Themes" defaultOpen>
          {/* Controls */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Search by name, description, or ID"
              />
            </div>
            <div className="flex gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="p-2 border border-gray-300 rounded-md"
                >
                  <option value="updatedAt">Last Updated</option>
                  <option value="name">Name</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
                <select
                  value={sortDir}
                  onChange={(e) => setSortDir(e.target.value)}
                  className="p-2 border border-gray-300 rounded-md"
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Per Page</label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value))}
                  className="p-2 border border-gray-300 rounded-md"
                >
                  <option value={6}>6</option>
                  <option value={9}>9</option>
                  <option value={12}>12</option>
                  <option value={18}>18</option>
                </select>
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {pagedThemes.map(theme => (
              <div
                key={theme.id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  currentTheme?.id === theme.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => selectTheme(theme)}
              >
                <h3 className="font-medium">{theme.name}</h3>
                <p className="text-sm text-gray-600">{theme.description}</p>
                <div className="text-xs text-gray-500 mt-2">
                  Updated: {new Date(theme.updatedAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages} · {sortedThemes.length} total
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1 border rounded-md disabled:opacity-50"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                Prev
              </button>
              <button
                type="button"
                className="px-3 py-1 border rounded-md disabled:opacity-50"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>

          <CreateThemeForm onCreate={createNewTheme} disabled={loading} />
        </CollapsibleSection>

        {/* Theme Editor */}
        {currentTheme && (
          <ThemeEditor
            theme={currentTheme}
            onUpdate={updateTheme}
            onUploadAsset={uploadAsset}
            onDeleteAsset={deleteAsset}
            onUpdateIntermissions={handleIntermissionUpdate}
            disabled={loading}
            setCurrentTheme={setCurrentTheme}
            setThemes={setThemes}
            themes={themes}
          />
        )}
      </div>
    </div>
  );
};

// Component for creating new themes
const CreateThemeForm = ({ onCreate, disabled }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate(name.trim(), description.trim());
      setName('');
      setDescription('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t pt-4">
      <h3 className="text-lg font-medium mb-3">Create New Theme</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Theme Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="My Custom Theme"
            required
            maxLength={128}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="A description of your theme"
            rows="3"
            maxLength={1024}
            disabled={disabled}
          />
        </div>
      </div>
      <button
        type="submit"
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        disabled={disabled || !name.trim()}
      >
        Create Theme
      </button>
    </form>
  );
};

// Background Image Section Component (for Instructions/Launch Screen - 1920x1080)
const BackgroundImageSection = ({ theme, disabled, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = React.useRef(null);

  const getAssetUrl = (filename) => {
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const basePath = theme.s3BasePath || `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`;
    return `${basePath}/${filename}`;
  };

  const handleUpload = async (file) => {
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/themes/${theme.id}/background`, {
        method: 'POST',
        headers: {
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      
      // Update theme with new background image
      const updatedTheme = { ...theme, backgroundImage: result.filename };
      onUpdate(updatedTheme);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the background image?')) {
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await fetch(`/api/themes/${theme.id}/background`, {
        method: 'DELETE',
        headers: {
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      // Update theme to remove background image
      const updatedTheme = { ...theme, backgroundImage: null };
      onUpdate(updatedTheme);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const backgroundImage = theme.backgroundImage;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        JPG/PNG image, 16:9 aspect ratio (1920x1080px recommended), for game launch screen background
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {backgroundImage ? (
        <div className="space-y-4">
          <div className="border border-gray-300 rounded-md overflow-hidden">
            <img
              src={getAssetUrl(backgroundImage)}
              alt="Background"
              className="w-full h-auto"
              style={{ maxHeight: '400px', objectFit: 'contain' }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Replace Background'}
            </button>
            <button
              onClick={handleDelete}
              disabled={disabled || uploading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Upload Background Image'}
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

// Cover Image Section Component
const CoverImageSection = ({ theme, disabled, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = React.useRef(null);

  const getAssetUrl = (filename, assetType) => {
    // Construct URL using s3BasePath from theme and subfolder based on asset type
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const basePath = theme.s3BasePath || `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`;
    const subfolder = assetType === 'cover' ? '' : `${assetType}/`;
    return `${basePath}/${subfolder}${filename}`;
  };

  const handleUpload = async (file) => {
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/themes/${theme.id}/cover`, {
        method: 'POST',
        headers: {
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      
      // Update theme with new cover image
      const updatedTheme = { ...theme, coverImage: result.filename };
      onUpdate(updatedTheme);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the cover image?')) {
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await fetch(`/api/themes/${theme.id}/cover`, {
        method: 'DELETE',
        headers: {
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      // Update theme to remove cover image
      const updatedTheme = { ...theme, coverImage: null };
      onUpdate(updatedTheme);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        JPG image, 2:1 aspect ratio (e.g., 1200x600px or 1600x800px), for shop display
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {theme.coverImage ? (
        <div className="space-y-4">
          <div className="border border-gray-300 rounded-md overflow-hidden">
            <img
              src={getAssetUrl(theme.coverImage, 'cover')}
              alt="Cover"
              className="w-full h-auto"
              style={{ maxHeight: '300px', objectFit: 'contain' }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Replace Cover Image'}
            </button>
            <button
              onClick={handleDelete}
              disabled={disabled || uploading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Upload Cover Image'}
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

// Main theme editor component
const ThemeEditor = ({ theme, onUpdate, onUploadAsset, onDeleteAsset, onUpdateIntermissions, disabled, setCurrentTheme, setThemes, themes }) => {
  const [showJson, setShowJson] = useState(false);

  // Ensure theme.assets exists with all required properties
  const safeTheme = {
    ...theme,
    assets: {
      battlefields: [],
      players: [],
      music: [],
      screens: [],
      ...(theme.assets || {})
    }
  };

  return (
    <div className="space-y-6">
      {/* Theme Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{safeTheme.name}</h2>
            <p className="text-gray-600">{safeTheme.description}</p>
            <div className="text-sm text-gray-500 mt-2">
              ID: {safeTheme.id} | Version: {safeTheme.version}
            </div>
          </div>
          <button
            onClick={() => setShowJson(!showJson)}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            {showJson ? 'Hide' : 'Show'} JSON
          </button>
        </div>

        {/* JSON Preview */}
        {showJson && (
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-2">Theme JSON</h3>
            <pre className="bg-gray-100 p-4 rounded-md text-sm overflow-auto max-h-96">
              {JSON.stringify(safeTheme, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Cover Image Section */}
      <CollapsibleSection title="Cover Image">
        <CoverImageSection
          theme={safeTheme}
          disabled={disabled}
          onUpdate={onUpdate}
        />
      </CollapsibleSection>

      {/* Background Image Section */}
      <CollapsibleSection title="Background Image">
        <BackgroundImageSection
          theme={safeTheme}
          disabled={disabled}
          onUpdate={onUpdate}
        />
      </CollapsibleSection>

      {/* Asset Upload Sections */}
      <CollapsibleSection title="Battlefields">
        <BattlefieldsSection theme={safeTheme} disabled={disabled} />
      </CollapsibleSection>

      <CollapsibleSection title="Player Images">
        <PlayersSection theme={safeTheme} disabled={disabled} />

        {/* Classes Manager */}
        <div className="mt-6">
          <h4 className="font-medium mb-2">Classes</h4>
          <ClassesManager theme={safeTheme} setTheme={(t)=>{
            setCurrentTheme(t);
            setThemes(themes.map(x => x.id === t.id ? t : x));
          }} disabled={disabled} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Music">
        <MusicSection theme={safeTheme} disabled={disabled} />
      </CollapsibleSection>

      {/* Sounds (SFX) */}
      <CollapsibleSection title="Sounds (SFX)">
        <SoundsSection theme={safeTheme} disabled={disabled} />
      </CollapsibleSection>

      <CollapsibleSection title="Screen Images">
        <AssetSection
          title={null}
          description="JPG/PNG images for intermission screens, 1:1 aspect ratio"
          assetType="screens"
          assets={safeTheme.assets.screens}
          accept=".jpg,.jpeg,.png"
          onUpload={(file) => onUploadAsset('screens', file)}
          onDelete={(filename) => onDeleteAsset('screens', filename)}
          disabled={disabled}
          themeId={safeTheme.id}
        />
      </CollapsibleSection>

      {/* Intermission Builder */}
      <CollapsibleSection title="Intermission Screens">
        <IntermissionBuilder
          theme={safeTheme}
          onUpdateIntermissions={onUpdateIntermissions}
          disabled={disabled}
          embedded
        />
      </CollapsibleSection>
    </div>
  );
};

// Intermission builder component
const IntermissionBuilder = ({ theme, onUpdateIntermissions, disabled, embedded = true }) => {
  const [intermissions, setIntermissions] = useState(theme.intermissions || { screens: [] });
  const [editingScreen, setEditingScreen] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleSave = () => {
    onUpdateIntermissions(intermissions);
  };

  const addScreen = () => {
    const newScreen = {
      id: `screen_${Date.now()}`,
      title: '',
      subtitle: '',
      durationSeconds: 5,
      content: { type: 'message', message: '' },
      trigger: { type: 'players_remaining', operator: 'lte', value: 10 }
    };
    setIntermissions({
      ...intermissions,
      screens: [...intermissions.screens, newScreen]
    });
    setEditingScreen(newScreen);
    setShowForm(true);
  };

  const updateScreen = (updatedScreen) => {
    setIntermissions({
      ...intermissions,
      screens: intermissions.screens.map(s => s.id === updatedScreen.id ? updatedScreen : s)
    });
  };

  const deleteScreen = (screenId) => {
    if (window.confirm('Are you sure you want to delete this screen?')) {
      setIntermissions({
        ...intermissions,
        screens: intermissions.screens.filter(s => s.id !== screenId)
      });
    }
  };

  const moveScreen = (index, direction) => {
    const newScreens = [...intermissions.screens];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newScreens.length) {
      [newScreens[index], newScreens[newIndex]] = [newScreens[newIndex], newScreens[index]];
      setIntermissions({ ...intermissions, screens: newScreens });
    }
  };

  return (
    <div className={embedded ? '' : 'bg-white rounded-lg shadow-md p-6'}>
      {!embedded && (
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold">Intermission Screens</h3>
            <p className="text-gray-600">Configure custom intermission screens with different content types</p>
          </div>
          <div className="space-x-2">
            <button
              onClick={addScreen}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              disabled={disabled}
            >
              Add Screen
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={disabled}
            >
              Save Changes
            </button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end items-center mb-4 space-x-2">
          <button
            onClick={addScreen}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            disabled={disabled}
          >
            Add Screen
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            disabled={disabled}
          >
            Save Changes
          </button>
        </div>
      )}

      {/* Screen List */}
      <div className="space-y-4">
        {intermissions.screens.map((screen, index) => (
          <div key={screen.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="font-medium">{screen.title || 'Untitled Screen'}</h4>
                <p className="text-sm text-gray-600">{screen.subtitle}</p>
                <div className="text-xs text-gray-500 mt-1">
                  Content: {screen.content.type} |
                  Trigger: {screen.trigger.value} players remaining ({screen.trigger.operator}) |
                  Duration: {screen.durationSeconds}s
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => moveScreen(index, -1)}
                  disabled={index === 0}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveScreen(index, 1)}
                  disabled={index === intermissions.screens.length - 1}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  onClick={() => {
                    setEditingScreen(screen);
                    setShowForm(true);
                  }}
                  className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteScreen(screen.id)}
                  className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {intermissions.screens.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No intermission screens configured. Click "Add Screen" to get started.
        </div>
      )}

      {/* Screen Editor Modal */}
      {showForm && editingScreen && (
        <ScreenEditorModal
          screen={editingScreen}
          theme={theme}
          onSave={(updatedScreen) => {
            updateScreen(updatedScreen);
            setShowForm(false);
            setEditingScreen(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingScreen(null);
          }}
        />
      )}
    </div>
  );
};

// Screen editor modal component
const ScreenEditorModal = ({ screen, theme, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ ...screen });

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const updateContent = (field, value) => {
    setFormData({
      ...formData,
      content: { ...formData.content, [field]: value }
    });
  };

  const updateTrigger = (field, value) => {
    setFormData({
      ...formData,
      trigger: { ...formData.trigger, [field]: value }
    });
  };

  const updateAction = (field, value) => {
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        action: { ...formData.content.action, [field]: value }
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-semibold mb-4">Edit Intermission Screen</h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Screen ID *
                </label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => updateFormData('id', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => updateFormData('title', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subtitle
              </label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (seconds) *
                </label>
                <input
                  type="number"
                  value={formData.durationSeconds}
                  onChange={(e) => updateFormData('durationSeconds', parseInt(e.target.value))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min="1"
                  max="300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Music
                </label>
                <select
                  value={formData.music || ''}
                  onChange={(e) => updateFormData('music', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">No music</option>
                  {theme.assets.music.map(filename => (
                    <option key={filename} value={filename}>{filename}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Content Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content Type *
              </label>
              <select
                value={formData.content.type}
                onChange={(e) => updateContent('type', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="message">Message</option>
                <option value="messageImage">Message with Image</option>
                <option value="playerList">Player List</option>
                <option value="playerListWithAction">Player List with Action</option>
              </select>
            </div>

            {/* Dynamic Content Fields */}
            <ContentTypeFields
              contentType={formData.content.type}
              content={formData.content}
              theme={theme}
              onUpdate={updateContent}
              onUpdateAction={updateAction}
            />

            {/* Trigger */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Trigger Conditions</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trigger Type
                  </label>
                  <select
                    value={formData.trigger.type}
                    onChange={(e) => updateTrigger('type', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="players_remaining">Players Remaining</option>
                    <option value="battle_time_seconds">Battle Time (seconds)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Operator
                  </label>
                  <select
                    value={formData.trigger.operator}
                    onChange={(e) => updateTrigger('operator', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="lte">Less than or equal (≤)</option>
                    <option value="gte">Greater than or equal (≥)</option>
                    <option value="eq">Equal (=)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value
                  </label>
                  <input
                    type="number"
                    value={formData.trigger.value}
                    onChange={(e) => updateTrigger('value', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    min="1"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Screen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Dynamic content type fields component
const ContentTypeFields = ({ contentType, content, theme, onUpdate, onUpdateAction }) => {
  switch (contentType) {
    case 'message':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message *
          </label>
          <textarea
            value={content.message || ''}
            onChange={(e) => onUpdate('message', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            rows="3"
            placeholder="Enter your message here..."
            required
          />
        </div>
      );

    case 'messageImage':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message *
            </label>
            <textarea
              value={content.message || ''}
              onChange={(e) => onUpdate('message', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows="3"
              placeholder="Enter your message here..."
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image *
            </label>
            <select
              value={content.image || ''}
              onChange={(e) => onUpdate('image', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Select an image...</option>
              {theme.assets.screens.map(filename => (
                <option key={filename} value={filename}>{filename}</option>
              ))}
            </select>
          </div>
        </div>
      );

    case 'playerList':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={content.showInventory !== false}
                onChange={(e) => onUpdate('showInventory', e.target.checked)}
                className="mr-2"
              />
              Show Inventory
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Items Per Player Row
            </label>
            <input
              type="number"
              value={content.maxItemsPerPlayerRow || 10}
              onChange={(e) => onUpdate('maxItemsPerPlayerRow', parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md"
              min="1"
              max="20"
            />
          </div>
        </div>
      );

    case 'playerListWithAction':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content.showInventory !== false}
                  onChange={(e) => onUpdate('showInventory', e.target.checked)}
                  className="mr-2"
                />
                Show Inventory
              </label>
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content.showWinChance !== false}
                  onChange={(e) => onUpdate('showWinChance', e.target.checked)}
                  className="mr-2"
                />
                Show Win Chance
              </label>
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content.showCountdown !== false}
                  onChange={(e) => onUpdate('showCountdown', e.target.checked)}
                  className="mr-2"
                />
                Show Countdown
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Items Per Player Row
              </label>
              <input
                type="number"
                value={content.maxItemsPerPlayerRow || 10}
                onChange={(e) => onUpdate('maxItemsPerPlayerRow', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md"
                min="1"
                max="20"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h5 className="font-medium mb-3">Action Button</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Label *
                </label>
                <input
                  type="text"
                  value={content.action?.label || ''}
                  onChange={(e) => onUpdateAction('label', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Lock Bets"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Name *
                </label>
                <input
                  type="text"
                  value={content.action?.event || ''}
                  onChange={(e) => onUpdateAction('event', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., lock_bets"
                  required
                />
              </div>
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

// Reusable asset upload section component
const AssetSection = ({
  title,
  description,
  assetType,
  assets,
  accept,
  onUpload,
  onDelete,
  disabled,
  themeId,
  themeClasses,
  isAudio = false
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [newName, setNewName] = useState('');
  const [newClassName, setNewClassName] = useState('Warrior');

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onUploadWithMeta(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUploadWithMeta(file);
    }
  };

  const onUploadWithMeta = (file) => {
    if (assetType === 'players') {
      // Build multipart form manually to send additional fields
      const extra = { name: newName, className: newClassName };
      // Delegate to parent handler and pass extra fields if it accepts them
      if (onUpload) onUpload(file, extra);
      setNewName('');
      return;
    }
    // Fallback for other asset types uses provided handler
    onUpload(file);
  };

  const getAssetUrl = (filename) => {
    // Asset type is passed from parent via assetType prop
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const basePath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${themeId}`;
    const subfolder = assetType === 'cover' ? '' : `${assetType}/`;
    return `${basePath}/${subfolder}${filename}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {title && <h3 className="text-xl font-semibold mb-2">{title}</h3>}
      <p className="text-gray-600 mb-4">{description}</p>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
      >
        <div className="space-y-4">
          <div className="text-4xl">📁</div>
          <div>
            <p className="text-lg font-medium">Drop files here or click to browse</p>
            <p className="text-gray-500">Supported formats: {accept}</p>
          </div>
          {assetType === 'players' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Player Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={128}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Aria Nightblade"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                <select
                  value={newClassName}
                  onChange={(e) => setNewClassName(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  {(Array.isArray(themeClasses) && themeClasses.length ? themeClasses : ['Warrior']).map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <input
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            id={`file-${assetType}`}
            disabled={disabled}
          />
          <label
            htmlFor={`file-${assetType}`}
            className={`inline-block px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer hover:bg-blue-700 ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Choose File
          </label>
        </div>
      </div>

      {/* Asset Grid */}
      {assets.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium mb-3">Uploaded Assets ({assets.length})</h4>
          <div className={`${assetType === 'players' ? 'grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3' : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'}`}>
            {assets.map(filename => (
              <AssetThumbnail
                key={filename}
                filename={filename}
                url={getAssetUrl(filename)}
                onDelete={() => onDelete(filename)}
                isAudio={isAudio}
                disabled={disabled}
                objectFit={assetType === 'players' ? 'contain' : 'cover'}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Asset thumbnail component with expand/delete functionality
const AssetThumbnail = ({ filename, url, onDelete, isAudio, disabled, objectFit = 'cover' }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div className="relative group">
        <div
          className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer border"
          onClick={() => setExpanded(true)}
          style={{ maxWidth: '96px' }}
        >
          {isAudio ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-2xl">🎵</div>
              <audio controls className="w-full mt-2">
                <source src={url} type="audio/mpeg" />
              </audio>
            </div>
          ) : (
            <img
              src={url}
              alt={filename}
              className="w-full h-full"
              style={{ objectFit }}
              loading="lazy"
            />
          )}
        </div>

        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
          disabled={disabled}
          title="Delete asset"
        >
          ×
        </button>

        {/* Filename */}
        <div className="mt-1 text-xs text-gray-600 truncate" title={filename}>
          {filename}
        </div>
      </div>

      {/* Expanded modal */}
      {expanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setExpanded(false)}
        >
          <div className="max-w-4xl max-h-full p-4" onClick={(e) => e.stopPropagation()}>
            {isAudio ? (
              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-medium mb-4">{filename}</h3>
                <audio controls className="w-full">
                  <source src={url} type="audio/mpeg" />
                </audio>
                <button
                  onClick={() => setExpanded(false)}
                  className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md"
                >
                  Close
                </button>
              </div>
            ) : (
              <img
                src={url}
                alt={filename}
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ThemeCreator;

// Simple collapsible container
const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-lg shadow-md mb-6">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-6 py-4 border-b hover:bg-gray-50"
      >
        <span className="text-xl font-semibold text-left">{title}</span>
        <span className="text-gray-700 text-2xl leading-none select-none" aria-hidden>
          {open ? '▲' : '▼'}
        </span>
      </button>
      {open && (
        <div className="p-6">
          {children}
        </div>
      )}
    </div>
  );
};

const MusicSection = ({ theme, disabled }) => {
  const [tracks, setTracks] = useState(Array.isArray(theme.music?.battle) ? theme.music.battle : []);
  const [category, setCategory] = useState('battle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFile, setModalFile] = useState(null);
  const [modalCategory, setModalCategory] = useState('battle');
  const [modalTitle, setModalTitle] = useState('');
  const [modalArtist, setModalArtist] = useState('');

  useEffect(() => {
    const cur = theme.music && theme.music[category];
    setTracks(Array.isArray(cur) ? cur : []);
  }, [theme.music, category]);

  // Ensure tracks load from API if not available in props
  useEffect(() => {
    const has = theme.music && Array.isArray(theme.music[category]) && theme.music[category].length > 0;
    if (!has) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, theme.id]);

  const reload = async () => {
    const resp = await fetch(`/api/themes/normalized/${theme.id}?noDefaults=true`, { headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' } });
    const data = await resp.json();
    const cur = data.music && data.music[category];
    setTracks(Array.isArray(cur) ? cur : []);
  };

  const upload = async (file, title, artist) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    if (title) form.append('title', title);
    if (artist) form.append('artist', artist);
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/music`, { method: 'POST', headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }, body: form });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Upload failed');
      setSuccess('Track uploaded');
      await reload();
    } catch (e) { setError(e.message || 'Upload failed'); } finally { setLoading(false); }
  };

  const uploadWithCategory = async (file, cat, title, artist) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('category', cat);
    if (title) form.append('title', title);
    if (artist) form.append('artist', artist);
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/music`, { method: 'POST', headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }, body: form });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Upload failed');
      setSuccess('Track uploaded');
      await reload();
    } catch (e) { setError(e.message || 'Upload failed'); } finally { setLoading(false); }
  };

  const updateMeta = async (track_key, title, artist) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/music`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }, body: JSON.stringify({ track_key, title, artist, category }) });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Save failed');
      setSuccess('Track updated');
      await reload();
    } catch (e) { setError(e.message || 'Save failed'); } finally { setLoading(false); }
  };

  const removeTrack = async (track_key) => {
    if (!track_key) return;
    if (!window.confirm('Delete this track?')) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/music/${encodeURIComponent(track_key)}`, { method: 'DELETE', headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' } });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Delete failed');
      setSuccess('Track deleted');
      await reload();
    } catch (e) { setError(e.message || 'Delete failed'); } finally { setLoading(false); }
  };

  const s3 = theme.s3BasePath || '';
  const [pendingTitle, setPendingTitle] = useState('');
  const [pendingArtist, setPendingArtist] = useState('');
  const [edits, setEdits] = useState({});

  // Initialize editable metadata map when tracks list changes
  useEffect(() => {
    const next = {};
    (tracks || []).forEach(t => {
      next[t.key] = {
        title: (t.title || ''),
        artist: (t.artist || '')
      };
    });
    setEdits(next);
  }, [tracks]);

  return (
    <div className="space-y-4"
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.mp3')) { setError('Please drop an MP3 file'); return; }
        setModalFile(file);
        setModalCategory(category);
        const base = (file.name || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
        setModalTitle(base);
        setModalArtist('');
        setModalOpen(true);
      }}
    >
      {loading && <div className="p-2 bg-blue-100 text-blue-800 rounded">Saving...</div>}
      {error && <div className="p-2 bg-red-100 text-red-800 rounded">{error}</div>}
      {success && <div className="p-2 bg-green-100 text-green-800 rounded">{success}</div>}

      {dragOver && (
        <div className="p-6 border-2 border-dashed border-blue-500 rounded bg-blue-50 text-blue-800 text-center">
          Drop MP3 here to add metadata
        </div>
      )}

      <div className="flex items-end gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select className="p-2 border rounded" value={category} onChange={e => setCategory(e.target.value)}>
            <option value="battle">Battle</option>
            <option value="intermissions">Intermissions</option>
            <option value="finale">Finale</option>
            <option value="pregame">Pregame</option>
            <option value="victory">Victory</option>
            <option value="rewards">Rewards</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
          <input className="w-full p-2 border rounded" value={pendingTitle} onChange={e => setPendingTitle(e.target.value)} placeholder="Track title" />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Artist (optional)</label>
          <input className="w-full p-2 border rounded" value={pendingArtist} onChange={e => setPendingArtist(e.target.value)} placeholder="Artist" />
        </div>
        <label className={`px-4 py-2 bg-blue-600 text-white rounded cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          Upload MP3
          <input type="file" accept=".mp3" className="hidden" disabled={disabled} onChange={e => upload(e.target.files?.[0], pendingTitle, pendingArtist)} />
        </label>
      </div>

      <div className="space-y-2">
        {tracks.map(t => {
          const url = s3
            ? (t.key.includes('/') ? `${s3}/${t.key}` : `${s3}/music/${t.key}`)
            : (t.key.includes('/') ? `assets/${t.key}` : `assets/music/${t.key}`);
          const meta = edits[t.key] || { title: '', artist: '' };
          return (
            <div key={t.key} className="border rounded p-3 relative">
              <div className="absolute left-2 bottom-2 text-xs text-gray-600 bg-white bg-opacity-80 px-1 rounded" title={`DB ID: ${t.id || 'n/a'}`}>ID: {t.id ?? 'n/a'}</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                <audio controls className="md:col-span-2 w-full"><source src={url} /></audio>
                <input className="p-2 border rounded" value={meta.title} onChange={e => setEdits(prev => ({ ...prev, [t.key]: { ...(prev[t.key]||{}), title: e.target.value } }))} placeholder="Title" />
                <input className="p-2 border rounded" value={meta.artist} onChange={e => setEdits(prev => ({ ...prev, [t.key]: { ...(prev[t.key]||{}), artist: e.target.value } }))} placeholder="Artist" />
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button className="px-3 py-2 border rounded" onClick={() => removeTrack(t.key)} disabled={disabled}>Delete</button>
                <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => updateMeta(t.key, (edits[t.key]?.title)||'', (edits[t.key]?.artist)||'')} disabled={disabled}>Save</button>
              </div>
            </div>
          );
        })}
        {(!tracks || tracks.length === 0) && (
          <div className="text-sm text-gray-500">No tracks in this category.</div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-3">Add Music</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select className="p-2 border rounded w-full" value={modalCategory} onChange={e => setModalCategory(e.target.value)}>
                  <option value="battle">Battle</option>
                  <option value="intermissions">Intermissions</option>
                  <option value="finale">Finale</option>
                  <option value="pregame">Pregame</option>
                  <option value="victory">Victory</option>
                  <option value="rewards">Rewards</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input className="w-full p-2 border rounded" value={modalTitle} onChange={e => setModalTitle(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Artist</label>
                <input className="w-full p-2 border rounded" value={modalArtist} onChange={e => setModalArtist(e.target.value)} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="px-3 py-2 border rounded" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={async () => { await uploadWithCategory(modalFile, modalCategory, modalTitle, modalArtist); setModalOpen(false); setModalFile(null); }}>Upload</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const BattlefieldsSection = ({ theme, disabled }) => {
  const [list, setList] = useState(Array.isArray(theme.battlefields) ? theme.battlefields : []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { setList(Array.isArray(theme.battlefields) ? theme.battlefields : []); }, [theme.battlefields]);

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/battlefields`, {
        method: 'POST',
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' },
        body: form
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Upload failed');
      setSuccess('Battlefield uploaded');
      await reload();
    } catch (e) { setError(e.message || 'Upload failed'); } finally { setLoading(false); }
  };

  const save = async (rows) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const merged = rows.map(r => ({
        id: r.id, name: r.name, theme: r.theme, active: !!r.active,
        size: r.size, image: r.image, description: r.description,
        object_scale: r.object_scale,
        margin: { top: r.margin?.top, bottom: r.margin?.bottom, left: r.margin?.left, right: r.margin?.right }
      }));
      const resp2 = await fetch(`/api/themes/${theme.id}/battlefields`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' },
        body: JSON.stringify(merged)
      });
      if (!resp2.ok) throw new Error((await resp2.json()).error || 'Save failed');
      setSuccess('Battlefields saved');
      await reload();
    } catch (e) { setError(e.message || 'Save failed'); } finally { setLoading(false); }
  };

  const reload = async () => {
    const resp = await fetch(`/api/themes/normalized/${theme.id}?noDefaults=true`, { headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' } });
    const data = await resp.json();
    setList(Array.isArray(data.battlefields) ? data.battlefields : []);
  };

  const remove = async (filename) => {
    if (!filename) return;
    if (!window.confirm('Delete this battlefield?')) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/battlefields/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Delete failed');
      setSuccess('Battlefield deleted');
      await reload();
    } catch (e) { setError(e.message || 'Delete failed'); } finally { setLoading(false); }
  };

  const s3 = theme.s3BasePath || '';

  return (
    <div className="space-y-4">
      {loading && <div className="p-2 bg-blue-100 text-blue-800 rounded">Saving...</div>}
      {error && <div className="p-2 bg-red-100 text-red-800 rounded">{error}</div>}
      {success && <div className="p-2 bg-green-100 text-green-800 rounded">{success}</div>}

      <div>
        <label className={`px-4 py-2 bg-blue-600 text-white rounded cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          Upload Battlefield (JPG)
          <input type="file" accept=".jpg,.jpeg" className="hidden" disabled={disabled} onChange={e => upload(e.target.files?.[0])} />
        </label>
      </div>

      <div className="space-y-3">
        {list.map((bf, idx) => (
          <div key={bf.id || bf.image} className="border rounded p-3">
            <div className="flex items-center gap-3">
              {bf.image && (
                <img
                  src={s3 ? `${s3}/battlefields/${bf.image}` : `assets/battlefields/${bf.image}`}
                  alt={bf.name}
                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                  onClick={() => setExpanded(s3 ? `${s3}/battlefields/${bf.image}` : `assets/battlefields/${bf.image}`)}
                />
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 flex-1">
                <label className="text-sm text-gray-700">Name
                  <input className="p-2 border rounded w-full" value={bf.name || ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, name: e.target.value } : x))} placeholder="Name" />
                </label>
                <label className="text-sm text-gray-700">Theme
                  <input className="p-2 border rounded w-full" value={bf.theme || ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, theme: e.target.value } : x))} placeholder="Theme" />
                </label>
                <label className="text-sm text-gray-700">Size
                <select className="p-2 border rounded w-full" value={bf.size || 'large'} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, size: e.target.value } : x))}>
                  <option value="small">small</option>
                  <option value="large">large</option>
                  <option value="largew">largew</option>
                </select>
                </label>
                <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!bf.active} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, active: e.target.checked } : x))} /> Active</label>
                <label className="text-sm text-gray-700">Object Scale
                  <input className="p-2 border rounded w-full" type="number" step="0.1" value={bf.object_scale ?? ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, object_scale: parseFloat(e.target.value) } : x))} placeholder="2.0 (large), 1.0 (small)" />
                </label>
                <label className="text-sm text-gray-700 md:col-span-3">Description
                  <input className="p-2 border rounded w-full" value={bf.description || ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, description: e.target.value } : x))} placeholder="Description" />
                </label>
                <div className="grid grid-cols-4 gap-2 md:col-span-3">
                  <label className="text-sm text-gray-700">Top %
                    <input className="p-2 border rounded w-full" type="number" step="0.1" value={bf.margin?.top ?? ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, margin: { ...x.margin, top: parseFloat(e.target.value) } } : x))} />
                  </label>
                  <label className="text-sm text-gray-700">Bottom %
                    <input className="p-2 border rounded w-full" type="number" step="0.1" value={bf.margin?.bottom ?? ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, margin: { ...x.margin, bottom: parseFloat(e.target.value) } } : x))} />
                  </label>
                  <label className="text-sm text-gray-700">Left %
                    <input className="p-2 border rounded w-full" type="number" step="0.1" value={bf.margin?.left ?? ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, margin: { ...x.margin, left: parseFloat(e.target.value) } } : x))} />
                  </label>
                  <label className="text-sm text-gray-700">Right %
                    <input className="p-2 border rounded w-full" type="number" step="0.1" value={bf.margin?.right ?? ''} onChange={e => setList(prev => prev.map((x,i) => i===idx ? { ...x, margin: { ...x.margin, right: parseFloat(e.target.value) } } : x))} />
                  </label>
                </div>
              </div>
              {bf.image && (
                <button className="ml-2 px-3 py-2 bg-red-600 text-white rounded" disabled={disabled} onClick={() => remove(bf.image)}>Delete</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="text-right">
        <button className="px-4 py-2 bg-blue-600 text-white rounded" disabled={disabled} onClick={() => save(list)}>Save Battlefields</button>
      </div>
      {expanded && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={() => setExpanded(null)}>
          <div className="max-w-5xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            <img src={expanded} alt="Battlefield" className="max-w-full max-h-[82vh] object-contain rounded" />
            <div className="text-right mt-3">
              <button className="px-3 py-2 bg-gray-600 text-white rounded" onClick={() => setExpanded(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Players advanced section with edit modal
const PlayersSection = ({ theme, disabled }) => {
  const [players, setPlayers] = useState(Array.isArray(theme.players) ? theme.players : []);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { setPlayers(Array.isArray(theme.players) ? theme.players : []); }, [theme.players]);

  const uploadBase = async (file, meta) => {
    const form = new FormData();
    form.append('file', file);
    if (meta?.name) form.append('name', meta.name);
    if (meta?.className) form.append('className', meta.className);
    if (meta?.description) form.append('description', meta.description);
    if (meta?.abilities) form.append('abilities', meta.abilities);
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/players`, {
        method: 'POST',
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' },
        body: form
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Upload failed');
      setSuccess('Player added');
      // reload theme players list
      await reloadPlayers();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally { setLoading(false); }
  };

  const uploadSpotlight = async (playerId, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/players/${playerId}/spotlight`, {
        method: 'POST',
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' },
        body: form
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Upload failed');
      setSuccess('Spotlight updated');
      await reloadPlayers();
    } catch (e) { setError(e.message || 'Upload failed'); } finally { setLoading(false); }
  };

  const saveMeta = async (p) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/players/${p.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({ name: p.name, className: p.className, description: p.description, abilities: p.abilities })
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Save failed');
      setSuccess('Player saved');
      await reloadPlayers();
      setEditing(null);
    } catch (e) { setError(e.message || 'Save failed'); } finally { setLoading(false); }
  };

  const reloadPlayers = async () => {
    try {
      const resp = await fetch(`/api/themes/normalized/${theme.id}?noDefaults=true`, {
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }
      });
      const data = await resp.json();
      setPlayers(Array.isArray(data.players) ? data.players : []);
    } catch (e) {}
  };

  const s3 = theme.s3BasePath || '';

  return (
    <div className="space-y-4">
      {loading && <div className="p-2 bg-blue-100 text-blue-800 rounded">Saving...</div>}
      {error && <div className="p-2 bg-red-100 text-red-800 rounded">{error}</div>}
      {success && <div className="p-2 bg-green-100 text-green-800 rounded">{success}</div>}

      <div className="mb-4">
        <label className={`px-4 py-2 bg-blue-600 text-white rounded cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          Add Player (PNG)
          <input type="file" accept=".png" className="hidden" disabled={disabled} onChange={e => uploadBase(e.target.files?.[0], { className: 'Warrior' })} />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {players.map(p => (
          <div key={p.id || p.image} className="border rounded p-3">
            <div className="flex items-center gap-3">
              <img src={s3 ? `${s3}/players/${p.image}` : `assets/players/${p.image}`} alt={p.name} style={{ width: 64, height: 64, objectFit: 'cover' }} />
              <div className="flex-1">
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-gray-500">{p.className}</div>
              </div>
              <button className="px-2 py-1 bg-gray-200 rounded" onClick={() => setEditing(p)}>Edit</button>
            </div>
            <div className="mt-3 text-sm text-gray-700 line-clamp-3">{p.description}</div>
            <div className="mt-2 text-xs text-gray-500 whitespace-pre-wrap">{p.abilities}</div>
            <div className="mt-3">
              <div className="text-sm font-medium mb-1">Spotlight</div>
              <label className="inline-block">
                {p.spotlightImage ? (
                  <img src={s3 ? `${s3}/players/${p.spotlightImage}` : `assets/players/${p.spotlightImage}`} alt="spotlight" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }} />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 flex items-center justify-center text-xl text-gray-400 border rounded cursor-pointer">+</div>
                )}
                <input type="file" accept=".png,.jpg,.jpeg" className="hidden" disabled={disabled} onChange={e => uploadSpotlight(p.id, e.target.files?.[0])} />
              </label>
            </div>

            {/* Player Taunts */}
            <PlayerTaunts themeId={theme.id} player={p} disabled={disabled} onChanged={reloadPlayers} />
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-3">Edit Player</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">Name</label>
                <input className="w-full p-2 border rounded" value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium">Class</label>
                <input className="w-full p-2 border rounded" value={editing.className || ''} onChange={e => setEditing({ ...editing, className: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">Description</label>
                <textarea className="w-full p-2 border rounded" rows={3} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">Abilities (text)</label>
                <textarea className="w-full p-2 border rounded" rows={4} value={editing.abilities || ''} onChange={e => setEditing({ ...editing, abilities: e.target.value })} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="px-3 py-2 border rounded" onClick={() => setEditing(null)}>Cancel</button>
              <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => saveMeta(editing)}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PlayerTaunts = ({ themeId, player, disabled, onChanged }) => {
  const [local, setLocal] = useState(Array.isArray(player.taunts) ? player.taunts : []);
  const [newTaunt, setNewTaunt] = useState('');
  const [tauntEvent, setTauntEvent] = useState('');
  useEffect(() => { setLocal(Array.isArray(player.taunts) ? player.taunts : []); }, [player.taunts]);

  const add = async () => {
    const t = (newTaunt || '').trim();
    if (!t) return;
    try {
      await fetch(`/api/themes/${themeId}/players/${player.id}/taunts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' },
        body: JSON.stringify({ text: t, event: tauntEvent || null })
      });
      setNewTaunt('');
      setTauntEvent('');
      onChanged && onChanged();
    } catch (_) {}
  };

  const remove = async (t) => {
    try {
      await fetch(`/api/themes/${themeId}/players/${player.id}/taunts?text=${encodeURIComponent(t)}`, {
        method: 'DELETE',
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }
      });
      onChanged && onChanged();
    } catch (_) {}
  };

  const getEventColor = (event) => {
    const colors = {
      attack: 'bg-red-100 text-red-800',
      hit: 'bg-orange-100 text-orange-800',
      hurt: 'bg-yellow-100 text-yellow-800',
      killed: 'bg-purple-100 text-purple-800',
      death: 'bg-gray-100 text-gray-800',
      threaten: 'bg-red-200 text-red-900',
      food: 'bg-green-100 text-green-800',
      pickup: 'bg-blue-100 text-blue-800',
      run: 'bg-indigo-100 text-indigo-800'
    };
    return colors[event] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="mt-3">
      <div className="text-sm font-medium mb-1">Taunts</div>
      <form className="flex gap-2 mb-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <input className="flex-1 p-2 border rounded" value={newTaunt} onChange={e => setNewTaunt(e.target.value)} placeholder="Add taunt... (press Enter)" disabled={disabled} />
        <select value={tauntEvent} onChange={e => setTauntEvent(e.target.value)} className="px-3 py-2 border rounded" disabled={disabled}>
          <option value="">No event</option>
          <option value="attack">Attack</option>
          <option value="hit">Hit</option>
          <option value="hurt">Hurt</option>
          <option value="killed">Killed</option>
          <option value="death">Death</option>
          <option value="threaten">Threaten</option>
          <option value="food">Food</option>
          <option value="pickup">Pickup</option>
          <option value="run">Run</option>
        </select>
        <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded" disabled={disabled}>Add</button>
      </form>
      <div className="flex flex-wrap gap-2">
        {local.map(obj => (
          <span key={obj.id || obj.text} className="px-2 py-1 bg-gray-100 border rounded text-sm inline-flex items-center gap-2">
            {obj.text || obj}
            {obj.event && (
              <span className={`px-2 py-1 text-xs rounded ${getEventColor(obj.event)}`}>
                {obj.event}
              </span>
            )}
            <button className="text-red-600" onClick={() => remove(obj.text || obj)} disabled={disabled}>×</button>
          </span>
        ))}
        {(!local || local.length === 0) && (
          <div className="text-xs text-gray-500">No taunts. Player will use generic taunts if available.</div>
        )}
      </div>
    </div>
  );
};


// Sounds upload/manage section
const SoundsSection = ({ theme, disabled }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sounds, setSounds] = useState(theme.sounds || {});

  const categories = [
    'arrow_hit','arrow_whoosh','attack_grunt','breath_tired','death',
    'melee_gore','melee_hit','melee_whoosh','pain','scream_big','scream_mad','taunt','weapon_hit','food_eating'
  ];

  useEffect(() => { setSounds(theme.sounds || {}); }, [theme.sounds]);

  // Ensure sounds are visible even if props didn't include them yet
  useEffect(() => {
    const hasAny = sounds && Object.keys(sounds || {}).some(k => (sounds[k] || []).length > 0);
    if (!hasAny) {
      (async () => {
        try {
          const resp = await fetch(`/api/themes/normalized/${theme.id}?noDefaults=true`, {
            headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }
          });
          if (!resp.ok) return;
          const data = await resp.json();
          if (data && data.sounds) setSounds(data.sounds);
        } catch (_) {}
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id]);

  const reload = async () => {
    try {
      setLoading(true);
      // Use noDefaults=true to only show sounds for this theme, not defaults
      const resp = await fetch(`/api/themes/normalized/${theme.id}?noDefaults=true`, {
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }
      });
      const data = await resp.json();
      setSounds((data && data.sounds) ? data.sounds : {});
    } catch (e) {
      setError('Failed to reload sounds');
    } finally {
      setLoading(false);
    }
  };

  const upload = async (category, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/sounds?category=${encodeURIComponent(category)}`, {
        method: 'POST',
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' },
        body: form
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Upload failed');
      setSuccess(`Uploaded to ${category}`);
      await reload();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (category, filename) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const resp = await fetch(`/api/themes/${theme.id}/assets/sounds/${encodeURIComponent(category)}/${encodeURIComponent(filename.split('/').pop())}`, {
        method: 'DELETE',
        headers: { 'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key' }
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Delete failed');
      setSuccess('Deleted');
      await reload();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const s3Base = theme.s3BasePath || '';

  return (
    <div className="space-y-4">
      {loading && <div className="p-2 bg-blue-100 text-blue-800 rounded">Processing...</div>}
      {error && <div className="p-2 bg-red-100 text-red-800 rounded">{error}</div>}
      {success && <div className="p-2 bg-green-100 text-green-800 rounded">{success}</div>}
      {categories.map(cat => (
        <div key={cat} className="border rounded p-4">
          <div className="flex items-center mb-2">
            <div className="flex items-center gap-2">
              <h4 className="font-medium">{cat}</h4>
              <label className={`w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full cursor-pointer select-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} title={`Add sound to ${cat}`} aria-label={`Add sound to ${cat}`}>
                +
                <input type="file" accept=".mp3,.ogg,.wav" className="hidden" disabled={disabled} onChange={e => upload(cat, e.target.files?.[0])} />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {(sounds[cat] || []).map((entry, idx) => {
              const id = (entry && typeof entry === 'object') ? (entry.id ?? `${cat}-${idx}`) : `${cat}-${idx}`;
              const rel = typeof entry === 'object' && entry ? (entry.file ?? '') : entry;
              const originalFile = (entry && typeof entry === 'object') ? entry.originalFile : null;
              const url = s3Base ? `${s3Base}/${rel}` : `assets/${rel}`;
              const file = (rel || '').split('/').pop();
              return (
                <div key={id} className="flex flex-col border rounded p-2">
                  <div className="text-xs text-gray-500 mb-1 truncate" title={file}>{file}</div>
                  {originalFile && (
                    <div className="text-xs text-gray-400 mb-1 truncate" title={originalFile}>Original: {originalFile}</div>
                  )}
                  <div className="flex items-center gap-3">
                    <audio controls className="flex-1">
                      <source src={url} />
                    </audio>
                    <button className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center flex-shrink-0" title="Delete" aria-label="Delete" disabled={disabled} onClick={() => remove(cat, rel)}>
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
            {(!sounds[cat] || sounds[cat].length === 0) && (
              <div className="text-sm text-gray-500">No sounds uploaded. Defaults will be used.</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Classes Manager component
const ClassesManager = ({ theme, setTheme, disabled }) => {
  const [local, setLocal] = useState(Array.isArray(theme.classes) ? theme.classes : ['Warrior']);
  const [newClass, setNewClass] = useState('');

  useEffect(() => {
    setLocal(Array.isArray(theme.classes) ? theme.classes : ['Warrior']);
  }, [theme.classes]);

  const addClass = () => {
    const c = (newClass || '').trim();
    if (!c) return;
    if (c.length > 64) return;
    if (local.includes(c)) return;
    setLocal([...local, c]);
    setNewClass('');
  };

  const removeClass = (c) => {
    if (c === 'Warrior') return; // keep default
    setLocal(local.filter(x => x !== c));
  };

  const save = async () => {
    try {
      const resp = await fetch(`/api/themes/${theme.id}/classes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
        body: JSON.stringify(local),
      });
      if (!resp.ok) throw new Error((await resp.json()).error || 'Failed');
      const updated = await resp.json();
      setTheme({ ...theme, classes: updated, updatedAt: new Date().toISOString() });
    } catch (e) {
      // silently ignore here; parent shows global errors
    }
  };

  return (
    <div className="bg-white border rounded p-4">
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newClass}
          onChange={(e) => setNewClass(e.target.value)}
          maxLength={64}
          className="flex-1 p-2 border border-gray-300 rounded-md"
          placeholder="Add new class (e.g., Mage)"
          disabled={disabled}
        />
        <button onClick={addClass} disabled={disabled} className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300">Add</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {local.map(c => (
          <span key={c} className="px-2 py-1 bg-gray-100 border rounded inline-flex items-center gap-2">
            {c}
            {c !== 'Warrior' && (
              <button onClick={() => removeClass(c)} disabled={disabled} className="text-red-600">×</button>
            )}
          </span>
        ))}
      </div>
      <div className="mt-3 text-right">
        <button onClick={save} disabled={disabled} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save Classes</button>
      </div>
    </div>
  );
};
